import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ExternalLink, FolderTree, ListTree, MessageSquareText, PanelRightClose, Reply, Search, Send, SlidersHorizontal } from "lucide-react";
import type { PrimaryNature } from "../../shared/guide";
import type { FixtureFiles } from "../lib/fixtures";
import type { HostedConversationComment, HostedReviewContext } from "../lib/hosted";
import { useLazySerializer } from "../hooks/useLazySerializer";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileTypeIcon } from "./FileTypeIcon";
import { ReviewCommentBody } from "./ReviewCommentBody";

interface TreeNode {
  name: string;
  path?: string;
  rank: number;
  children: Map<string, TreeNode>;
}

export interface FileReviewProgress {
  changedLoc: number;
  natures: PrimaryNature[];
  scrutiny: "scan" | "standard" | "deep";
  totalStops: number;
  visitedStops: number;
}

type TreeFilter = "all" | "since-review" | "core" | "deep" | "tests" | "generated";
type FilePanelMode = "steps" | "tree" | "conversation";

export interface FileStepGroup {
  id: string;
  index: number;
  paths: string[];
  scrutiny: "scan" | "standard" | "deep";
  title: string;
  totalStops: number;
  visitedStops: number;
}

const natureLabels: Partial<Record<PrimaryNature, string>> = {
  authorization: "Auth",
  "business-logic": "Logic",
  "controller-entrypoint": "Entry",
  contract: "Contract",
  "data-persistence": "Data",
  integration: "Integration",
  "tests-proof": "Test",
  generated: "Generated",
  "wiring-config": "Wiring",
  frontend: "UI",
  infrastructure: "Infra",
  "analytics-data-model": "Model",
};

function buildTree(paths: string[]) {
  const root: TreeNode = { name: "", rank: 0, children: new Map() };
  for (const [pathRank, path] of paths.entries()) {
    let node = root;
    const parts = path.split("/");
    for (const [index, part] of parts.entries()) {
      let child = node.children.get(part);
      if (!child) {
        child = { name: part, rank: pathRank, children: new Map() };
        node.children.set(part, child);
      } else child.rank = Math.min(child.rank, pathRank);
      node = child;
      if (index === parts.length - 1) node.path = path;
    }
  }
  return root;
}

function containsPath(node: TreeNode, path: string): boolean {
  if (node.path === path) return true;
  return [...node.children.values()].some((child) => containsPath(child, path));
}

function compactDirectory(node: TreeNode) {
  let compacted = node;
  let label = node.name;
  while (compacted.children.size === 1) {
    const onlyChild = [...compacted.children.values()][0];
    if (!onlyChild || onlyChild.path) break;
    compacted = onlyChild;
    label += `/${onlyChild.name}`;
  }
  return { label, node: compacted };
}

function branchProgress(
  node: TreeNode,
  progressByPath: Record<string, FileReviewProgress>,
): { total: number; visited: number } {
  if (node.path) {
    const progress = progressByPath[node.path];
    return { total: progress?.totalStops ?? 0, visited: progress?.visitedStops ?? 0 };
  }
  return [...node.children.values()].reduce(
    (total, child) => {
      const progress = branchProgress(child, progressByPath);
      return { total: total.total + progress.total, visited: total.visited + progress.visited };
    },
    { total: 0, visited: 0 },
  );
}

function TreeBranch({
  depth,
  expandAll,
  focusedPaths,
  changedSincePaths,
  node,
  onSelect,
  ordering,
  progressByPath,
  selectedPath,
  threadCountByPath,
  viewedPaths,
}: {
  depth: number;
  expandAll: boolean;
  focusedPaths: Set<string>;
  changedSincePaths: Set<string>;
  node: TreeNode;
  onSelect: (path: string) => void;
  ordering: "alphabetical" | "input";
  progressByPath: Record<string, FileReviewProgress>;
  selectedPath: string;
  threadCountByPath: Record<string, number>;
  viewedPaths: Set<string>;
}) {
  const children = [...node.children.values()].sort((left, right) => {
    if (ordering === "input") return left.rank - right.rank;
    const leftFile = Boolean(left.path);
    const rightFile = Boolean(right.path);
    return leftFile === rightFile ? left.name.localeCompare(right.name) : Number(leftFile) - Number(rightFile);
  });
  return (
    <>
      {children.map((child) => {
        if (child.path) {
          const selected = child.path === selectedPath;
          const focused = focusedPaths.has(child.path);
          const changedSinceReview = changedSincePaths.has(child.path);
          const viewed = viewedPaths.has(child.path);
          const progress = progressByPath[child.path];
          const complete = Boolean(progress?.totalStops && progress.visitedStops >= progress.totalStops);
          const partial = Boolean(progress?.visitedStops && !complete);
          const threadCount = threadCountByPath[child.path] ?? 0;
          const label = progress?.natures.map((nature) => natureLabels[nature] ?? nature).join(", ") ?? "Unclassified";
          return (
            <button
              aria-current={selected ? "true" : undefined}
              className={`file-tree-file scrutiny-${progress?.scrutiny ?? "scan"} nature-${progress?.natures[0] ?? "other"}${selected ? " selected" : ""}${focused ? " guided" : ""}${changedSinceReview ? " changed-since-review" : ""}${viewed ? " viewed" : ""}${complete ? " complete" : partial ? " partial" : ""}`}
              key={child.path}
              onClick={() => onSelect(child.path!)}
              style={{ paddingLeft: `${13 + depth * 13}px` }}
              title={`${child.path} · ${label} · ${progress?.changedLoc ?? 0} changed LOC${progress?.totalStops ? ` · ${progress.visitedStops}/${progress.totalStops} focal stops` : ""}${viewed ? " · marked viewed" : ""}`}
              type="button"
            >
              <span aria-hidden="true" className="file-tree-review-state">{viewed || complete ? "✓" : partial ? "◐" : focused ? "●" : ""}</span>
              <FileTypeIcon compact path={child.path} />
              <strong>{child.name}</strong>
              {threadCount > 0 && <span aria-label={`${threadCount} unresolved ${threadCount === 1 ? "thread" : "threads"}`} className="file-tree-thread-count"><MessageSquareText aria-hidden="true" />{threadCount}</span>}
              {changedSinceReview ? <small title="Changed since your latest review">New</small> : progress?.totalStops ? <small>{progress.visitedStops}/{progress.totalStops}</small> : null}
            </button>
          );
        }
        const compacted = compactDirectory(child);
        const open = expandAll || depth === 0 || containsPath(compacted.node, selectedPath) || [...focusedPaths].some((path) => containsPath(compacted.node, path));
        const progress = branchProgress(compacted.node, progressByPath);
        return (
          <details className="file-tree-directory" key={`${depth}:${compacted.label}`} open={open}>
            <summary style={{ paddingLeft: `${10 + depth * 13}px` }}>
              <span aria-hidden="true">⌄</span><strong>{compacted.label}</strong>
              {progress.total > 0 && <small title={`${progress.visited} of ${progress.total} focal stops visited in this folder`}>{progress.visited}/{progress.total}</small>}
            </summary>
            <TreeBranch
              depth={depth + 1}
              expandAll={expandAll}
              changedSincePaths={changedSincePaths}
              focusedPaths={focusedPaths}
              node={compacted.node}
              onSelect={onSelect}
              ordering={ordering}
              progressByPath={progressByPath}
              selectedPath={selectedPath}
              threadCountByPath={threadCountByPath}
              viewedPaths={viewedPaths}
            />
          </details>
        );
      })}
    </>
  );
}

function matchesTreeFilter(path: string, progress: FileReviewProgress | undefined, filter: TreeFilter, changedSincePaths: Set<string>) {
  if (filter === "all") return true;
  if (filter === "since-review") return changedSincePaths.has(path);
  if (!progress) return false;
  if (filter === "core") return !progress.natures.includes("tests-proof") && !progress.natures.includes("generated");
  if (filter === "deep") return progress.scrutiny === "deep";
  if (filter === "tests") return progress.natures.includes("tests-proof");
  return progress.natures.includes("generated");
}

function StepFileGroup({
  current,
  expandMatches,
  focusedPaths,
  changedSincePaths,
  group,
  onSelect,
  paths,
  progressByPath,
  selectedPath,
  threadCountByPath,
  viewedPaths,
}: {
  current: boolean;
  expandMatches: boolean;
  focusedPaths: Set<string>;
  changedSincePaths: Set<string>;
  group: FileStepGroup;
  onSelect: (path: string) => void;
  paths: string[];
  progressByPath: Record<string, FileReviewProgress>;
  selectedPath: string;
  threadCountByPath: Record<string, number>;
  viewedPaths: Set<string>;
}) {
  const [open, setOpen] = useState(current || paths.includes(selectedPath));
  const tree = useMemo(() => buildTree(paths), [paths]);

  useEffect(() => {
    if (current || expandMatches) setOpen(true);
  }, [current, expandMatches]);

  if (paths.length === 0) return null;
  return (
    <details
      className={`file-step-group scrutiny-${group.scrutiny}${current ? " current" : ""}`}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      open={expandMatches || open}
    >
      <summary>
        <span aria-hidden="true">›</span>
        <div>
          <small>Step {group.index + 1}</small>
          <strong>{group.title}</strong>
        </div>
        <em title={`${group.visitedStops} of ${group.totalStops} focal stops visited in this step`}>{group.visitedStops}/{group.totalStops}</em>
      </summary>
      <div className="file-step-tree">
        <TreeBranch
          depth={0}
          expandAll
          changedSincePaths={changedSincePaths}
          focusedPaths={focusedPaths}
          node={tree}
          onSelect={onSelect}
          ordering="input"
          progressByPath={progressByPath}
          selectedPath={selectedPath}
          threadCountByPath={threadCountByPath}
          viewedPaths={viewedPaths}
        />
      </div>
    </details>
  );
}

function activityAge(createdAt: string) {
  const elapsed = Math.max(0, Date.now() - Date.parse(createdAt));
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(new Date(createdAt));
}

function ConversationFeed({
  conversation,
  draftBody,
  onDraft,
  onReply,
  onSelectLocation,
  paths,
}: {
  conversation?: HostedReviewContext["conversation"];
  draftBody: string;
  onDraft: (body: string) => void | Promise<void>;
  onReply?: (commentId: number, body: string) => Promise<HostedConversationComment>;
  onSelectLocation: (path: string, line?: number, side?: "LEFT" | "RIGHT") => void;
  paths: string[];
}) {
  const [replyingTo, setReplyingTo] = useState<string>();
  const [replyBody, setReplyBody] = useState("");
  const [replyError, setReplyError] = useState<string>();
  const [sendingReply, setSendingReply] = useState(false);
  const [localReplies, setLocalReplies] = useState<Record<string, HostedConversationComment[]>>({});
  const [composerBody, setComposerBody] = useState(draftBody);
  const [composerDirty, setComposerDirty] = useState(false);
  const composerBodyRef = useRef(composerBody);
  const composerDirtyRef = useRef(false);
  const composerEditedRef = useRef(false);
  const draftSerializer = useLazySerializer(onDraft);

  useEffect(() => {
    if (composerEditedRef.current) {
      if (draftBody === composerBodyRef.current) {
        composerDirtyRef.current = false;
        setComposerDirty(false);
      }
      return;
    }
    composerBodyRef.current = draftBody;
    setComposerBody(draftBody);
  }, [draftBody]);

  const composer = (
    <div className="conversation-composer">
      <textarea
        aria-label="Add review comment"
        onChange={(event) => {
          const body = event.target.value;
          composerBodyRef.current = body;
          composerDirtyRef.current = true;
          composerEditedRef.current = true;
          setComposerBody(body);
          setComposerDirty(true);
          draftSerializer.schedule(body);
        }}
        placeholder="Add a review comment…"
        rows={3}
        value={composerBody}
      />
      <footer><span>{composerDirty ? "Saving locally…" : composerBody.trim() ? "Saved locally" : "Draft locally"}</span><small>Publishes with your review</small></footer>
    </div>
  );

  if (!conversation) {
    return (
      <div className="conversation-feed">
        {composer}
        <div className="conversation-empty">
          <MessageSquareText aria-hidden="true" />
          <strong>No hosted conversation loaded</strong>
          <p>Live PR comments and review threads appear here when GitHub context is available.</p>
        </div>
      </div>
    );
  }
  if (conversation.unavailableReason) {
    return <div className="conversation-feed">{composer}<div className="conversation-empty"><MessageSquareText aria-hidden="true" /><strong>Conversation unavailable</strong><p>{conversation.unavailableReason}</p></div></div>;
  }
  const threads = conversation.threads ?? [];
  const recentItems = threads.length > 0
    ? conversation.recent.filter((item) => item.kind === "conversation")
    : conversation.recent;

  return (
    <div className="conversation-feed">
      {composer}
      <div className="conversation-summary">
        <strong>{conversation.unresolvedThreadCount} unresolved</strong>
        <span>{conversation.reviewThreadCount} threads · {conversation.issueCommentCount} PR comments</span>
      </div>
      {threads.length > 0 && <ol className="conversation-thread-list">
        {threads
          .filter((thread) => thread.comments.length > 0)
          .sort((left, right) => Date.parse(right.comments.at(-1)!.createdAt) - Date.parse(left.comments.at(-1)!.createdAt))
          .map((thread) => {
            const first = thread.comments[0]!;
            const displayedComments = [...thread.comments, ...(localReplies[thread.id] ?? [])];
            const latest = displayedComments.at(-1)!;
            const location = `${thread.path.split("/").at(-1)}${thread.line ? `:${thread.line}` : ""}`;
            const replyOpen = replyingTo === thread.id;
            const canReply = Boolean(onReply && !thread.resolved && !thread.outdated);
            const submitReply = async () => {
              const body = replyBody.trim();
              if (!onReply || !body || sendingReply) return;
              setSendingReply(true);
              setReplyError(undefined);
              try {
                const reply = await onReply(first.id, body);
                setLocalReplies((current) => ({ ...current, [thread.id]: [...(current[thread.id] ?? []), reply] }));
                setReplyBody("");
                setReplyingTo(undefined);
              } catch (reason) {
                setReplyError(reason instanceof Error ? reason.message : "Could not send the reply.");
              } finally {
                setSendingReply(false);
              }
            };
            return (
              <li className={`review-thread thread-group${thread.resolved ? " resolved" : ""}${thread.outdated ? " outdated" : ""}`} key={thread.id}>
                <span aria-hidden="true" className="conversation-avatar">{first.author.slice(0, 1).toUpperCase()}</span>
                <article>
                  <header><strong>{displayedComments.length === 1 ? `@${first.author}` : `${displayedComments.length} replies`}</strong><time dateTime={latest.createdAt}>{activityAge(latest.createdAt)}</time><em>{thread.resolved ? "Resolved" : thread.outdated ? "Outdated" : "Open"}</em></header>
                  <div className="thread-replies">
                    {displayedComments.map((comment) => (
                      <div key={comment.url}>
                        <header><strong>@{comment.author}</strong><time dateTime={comment.createdAt}>{activityAge(comment.createdAt)}</time></header>
                        <p><ReviewCommentBody body={comment.body} codePaths={paths} onSelectLocation={onSelectLocation} /></p>
                      </div>
                    ))}
                  </div>
                  <footer>
                    <button onClick={() => onSelectLocation(thread.path, thread.line, thread.side)} title={thread.path} type="button"><span>{location}</span></button>
                    {canReply && <button className="thread-reply-trigger" onClick={() => { setReplyingTo(replyOpen ? undefined : thread.id); setReplyBody(""); setReplyError(undefined); }} type="button"><Reply aria-hidden="true" /><span>Reply</span></button>}
                    <a aria-label="Open thread on GitHub" href={latest.url} rel="noreferrer" target="_blank"><ExternalLink aria-hidden="true" /></a>
                  </footer>
                  {replyOpen && (
                    <div className="thread-reply-composer">
                      <textarea aria-label={`Reply to thread in ${location}`} autoFocus onChange={(event) => setReplyBody(event.target.value)} placeholder="Reply to this thread…" rows={3} value={replyBody} />
                      {replyError && <p role="alert">{replyError}</p>}
                      <footer><button onClick={() => setReplyingTo(undefined)} type="button">Cancel</button><button disabled={!replyBody.trim() || sendingReply} onClick={() => void submitReply()} type="button"><Send aria-hidden="true" />{sendingReply ? "Sending…" : "Send reply"}</button></footer>
                    </div>
                  )}
                </article>
              </li>
            );
          })}
      </ol>}
      {recentItems.length > 0 && <ol>
        {recentItems.map((item) => {
          const location = item.path ? `${item.path.split("/").at(-1)}${item.line ? `:${item.line}` : ""}` : undefined;
          return (
            <li className={`${item.kind}${item.resolved ? " resolved" : ""}${item.outdated ? " outdated" : ""}`} key={item.url}>
              <span aria-hidden="true" className="conversation-avatar">{item.author.slice(0, 1).toUpperCase()}</span>
              <article>
                <header>
                  <strong>@{item.author}</strong>
                  <time dateTime={item.createdAt}>{activityAge(item.createdAt)}</time>
                  {item.kind === "review-thread" && <em>{item.resolved ? "Resolved" : item.outdated ? "Outdated" : "Open"}</em>}
                </header>
                <p><ReviewCommentBody body={item.body} codePaths={paths} onSelectLocation={onSelectLocation} /></p>
                <footer>
                  {item.path && (
                    <button onClick={() => onSelectLocation(item.path!, item.line, item.side)} title={item.path} type="button">
                      <span>{location}</span>
                    </button>
                  )}
                  <a aria-label={`Open ${item.kind === "review-thread" ? "thread" : "comment"} on GitHub`} href={item.url} rel="noreferrer" target="_blank"><ExternalLink aria-hidden="true" /></a>
                </footer>
              </article>
            </li>
          );
        })}
      </ol>}
      {conversation.recent.length === 0 && <div className="conversation-empty"><Check aria-hidden="true" /><strong>No conversation yet</strong><p>This PR has no comments or review threads.</p></div>}
      {conversation.truncated && <p className="conversation-truncated">Showing the latest activity from the first 100 review threads.</p>}
    </div>
  );
}

export function FileTree({
  collapsed,
  conversation,
  conversationDraft,
  files,
  changedSincePaths,
  focusedPaths,
  onSelect,
  onSelectConversation,
  onConversationDraft,
  onReplyThread,
  onToggle,
  progressByPath,
  selectedPath,
  stepGroups,
  currentStepId,
  viewedPaths,
}: {
  collapsed: boolean;
  conversation?: HostedReviewContext["conversation"];
  conversationDraft: string;
  files: FixtureFiles;
  changedSincePaths: Set<string>;
  focusedPaths: Set<string>;
  onSelect: (path: string) => void;
  onSelectConversation: (path: string, line?: number, side?: "LEFT" | "RIGHT") => void;
  onConversationDraft: (body: string) => void | Promise<void>;
  onReplyThread?: (commentId: number, body: string) => Promise<HostedConversationComment>;
  onToggle: () => void;
  progressByPath: Record<string, FileReviewProgress>;
  selectedPath: string;
  stepGroups: FileStepGroup[];
  currentStepId: string;
  viewedPaths: Set<string>;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<TreeFilter>("all");
  const [panelMode, setPanelMode] = useState<FilePanelMode>(() => localStorage.getItem("chew:file-view") === "tree" ? "tree" : "steps");
  const paths = useMemo(() => Object.keys(files.files).sort(), [files.files]);
  const matchingPaths = useMemo(
    () => paths.filter((path) =>
      path.toLowerCase().includes(query.trim().toLowerCase()) && matchesTreeFilter(path, progressByPath[path], filter, changedSincePaths),
    ),
    [changedSincePaths, filter, paths, progressByPath, query],
  );
  const tree = useMemo(() => buildTree(matchingPaths), [matchingPaths]);
  const matchingPathSet = useMemo(() => new Set(matchingPaths), [matchingPaths]);
  const totals = useMemo(
    () => Object.values(progressByPath).reduce(
      (total, progress) => ({ total: total.total + progress.totalStops, visited: total.visited + progress.visitedStops }),
      { total: 0, visited: 0 },
    ),
    [progressByPath],
  );
  const threadCountByPath = useMemo(() => (conversation?.threads ?? []).reduce<Record<string, number>>((counts, thread) => {
    if (!thread.resolved && !thread.outdated) counts[thread.path] = (counts[thread.path] ?? 0) + 1;
    return counts;
  }, {}), [conversation?.threads]);

  useEffect(() => {
    if (panelMode !== "conversation") localStorage.setItem("chew:file-view", panelMode);
  }, [panelMode]);

  useEffect(() => {
    if ((conversation?.reviewThreadCount ?? 0) > 0 || (conversation?.issueCommentCount ?? 0) > 0) void import("./ReviewThreads.css");
  }, [conversation?.issueCommentCount, conversation?.reviewThreadCount]);

  useEffect(() => {
    const filters: Record<string, TreeFilter> = { "1": "all", "2": "core", "3": "deep", "4": "tests", "5": "generated", "6": "since-review" };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (document.querySelector(".shortcut-panel")) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, button, [contenteditable='true']")) return;
      const next = filters[event.key];
      if (!next) return;
      event.preventDefault();
      setFilter(next);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <nav aria-label="Changed files" className={`file-tree${collapsed ? " file-tree-collapsed" : ""}`}>
      <button aria-label="Expand file picker" className="file-tree-rail" onClick={onToggle} title="Expand files · ]" type="button">
        <span>{panelMode === "conversation" ? "Talk" : "Files"}</span><small title={panelMode === "conversation" ? "Unresolved review threads" : `${totals.visited} of ${totals.total} focal stops visited`}>{panelMode === "conversation" ? conversation?.unresolvedThreadCount ?? 0 : `${totals.visited}/${totals.total}`}</small><kbd>]</kbd>
      </button>
      <header>
        <div className="file-tree-titlebar">
          <div><strong>{panelMode === "conversation" ? "Conversation" : "Changes"}</strong><span>{panelMode === "conversation" ? `${conversation?.unresolvedThreadCount ?? 0} open` : `${paths.length} changed`}</span></div>
          <Button aria-label="Collapse file picker" onClick={onToggle} size="icon-xs" title="Collapse files · ]" type="button" variant="ghost"><PanelRightClose /></Button>
        </div>
        <div aria-label="Right panel view" className="file-panel-tabs" role="tablist">
          <button aria-selected={panelMode === "steps"} onClick={() => setPanelMode("steps")} role="tab" type="button"><ListTree aria-hidden="true" />Steps</button>
          <button aria-selected={panelMode === "tree"} onClick={() => setPanelMode("tree")} role="tab" type="button"><FolderTree aria-hidden="true" />Tree</button>
          <button aria-selected={panelMode === "conversation"} onClick={() => setPanelMode("conversation")} role="tab" type="button"><MessageSquareText aria-hidden="true" />Threads{Boolean(conversation?.unresolvedThreadCount) && <span>{conversation!.unresolvedThreadCount}</span>}</button>
        </div>
        {panelMode !== "conversation" && <>
          <div className="file-tree-tools">
            <label className="file-tree-search">
              <Search aria-hidden="true" />
              <input
                aria-label="Filter changed files"
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setQuery("");
                    event.currentTarget.blur();
                  }
                }}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find file"
                type="search"
                value={query}
              />
            </label>
            <Popover>
              <PopoverTrigger render={<Button aria-label="Filter files by review category" size="icon-xs" title={`Filter: ${filter}`} variant={filter === "all" ? "ghost" : "secondary"} />}>
                <SlidersHorizontal />
              </PopoverTrigger>
              <PopoverContent align="end" className="w-40 gap-0.5 p-1" sideOffset={5}>
                {(["all", "core", "deep", "tests", "generated", "since-review"] as TreeFilter[]).map((candidate, index) => (
                  <Button
                    className="w-full justify-between"
                    key={candidate}
                    onClick={() => setFilter(candidate)}
                    size="sm"
                    variant={filter === candidate ? "secondary" : "ghost"}
                  >
                    {{ all: "All files", "since-review": `Since review (${changedSincePaths.size})`, core: "Core code", deep: "Deep review", tests: "Tests", generated: "Generated" }[candidate]}
                    <kbd>{index + 1}</kbd>
                  </Button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        </>}
      </header>
      <div className={`file-tree-scroll${panelMode === "conversation" ? " conversation-scroll" : ""}`}>
        {panelMode === "conversation" ? (
          <ConversationFeed conversation={conversation} draftBody={conversationDraft} onDraft={onConversationDraft} onReply={onReplyThread} onSelectLocation={onSelectConversation} paths={paths} />
        ) : matchingPaths.length > 0 && panelMode === "steps" ? (
          <div className="file-step-groups">
            {stepGroups.map((group) => (
              <StepFileGroup
                current={group.id === currentStepId}
                changedSincePaths={changedSincePaths}
                expandMatches={Boolean(query.trim()) || filter !== "all"}
                focusedPaths={focusedPaths}
                group={group}
                key={group.id}
                onSelect={onSelect}
                paths={group.paths.filter((path) => matchingPathSet.has(path))}
                progressByPath={progressByPath}
                selectedPath={selectedPath}
                threadCountByPath={threadCountByPath}
                viewedPaths={viewedPaths}
              />
            ))}
          </div>
        ) : matchingPaths.length > 0 ? (
          <TreeBranch
            depth={0}
            expandAll={Boolean(query.trim()) || filter !== "all"}
            changedSincePaths={changedSincePaths}
            focusedPaths={focusedPaths}
            node={tree}
            onSelect={onSelect}
            ordering="alphabetical"
            progressByPath={progressByPath}
            selectedPath={selectedPath}
            threadCountByPath={threadCountByPath}
            viewedPaths={viewedPaths}
          />
        ) : <p>No matching files</p>}
      </div>
    </nav>
  );
}
